import React, { FC } from "react";
import styled from "styled-components";
import { vwMobile, vwDesktop, rem } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import SectionLines from "../SectionLines";

export type ProcessAndPeopleProps = {
  headline: string;
  description: string;
  buttonLabel: string;
  buttonUrl: string;
  imageUrl: string;
  imageAlt: string;
  className?: string;
};

const ProcessAndPeople: FC<ProcessAndPeopleProps> = ({
  headline,
  description,
  buttonLabel,
  buttonUrl,
  imageUrl,
  imageAlt,
  className,
}: ProcessAndPeopleProps) => {
  return (
    <Section className={className} aria-labelledby="process-and-people-title">
      <Inner>
        <Grid>
          <ContentCol>
            <Headline id="process-and-people-title">{headline}</Headline>
            <Description
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            ></Description>
            <PrimaryCta href={buttonUrl}>{buttonLabel}</PrimaryCta>
          </ContentCol>

          <ImageCol>
            <ImageWrap>
              <Image
                src={imageUrl}
                alt={imageAlt}
                loading="lazy"
                decoding="async"
              />
            </ImageWrap>
          </ImageCol>
        </Grid>
      </Inner>
    </Section>
  );
};

export default ProcessAndPeople;

const Section = styled.section`
  background: #012604;
  position: relative;
  ${SectionLines("#6ab437")};
`;

const Inner = styled.div`
  padding-top: ${vwMobile(64)};
  padding-bottom: ${vwMobile(64)};
  padding-left: ${vwMobile(20)};
  padding-right: ${vwMobile(20)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${vwDesktop(120)};
    padding-bottom: ${vwDesktop(120)};
    padding-left: ${vwDesktop(128)};
    padding-right: ${vwDesktop(80)};
  }
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(50)};
  text-align: center;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    text-align: left;
    flex-direction: row;
    align-items: center;
    gap: ${vwDesktop(48)};
  }
`;

const ContentCol = styled.div`
  flex: 1 1 50%;
  order: 2;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    order: 1;
  }
`;

const ImageCol = styled.div`
  flex: 1 1 50%;
  order: 1;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    order: 2;
  }
`;

const Headline = styled.h2`
  margin: 0;
  color: #b5dd71;
  font-family: "GT Alpina";
  font-size: ${rem(48)};
  font-style: normal;
  font-weight: 300;
  line-height: 100%;
  letter-spacing: ${rem(-2.4)};
  text-align: center;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    text-align: unset;
    line-height: 95%;
    font-size: ${rem(72)};
    letter-spacing: ${rem(-3.6)};
  }
`;

const Description = styled.p`
  color: #f6f1e7;
  font-family: "Sharp Sans Display No1";
  font-size: ${rem(12)};
  font-style: normal;
  font-weight: 500;
  line-height: 130%;

  padding-top: ${vwMobile(20)};
  padding-bottom: ${vwMobile(20)};

  padding-left: ${vwMobile(20)};
  padding-right: ${vwMobile(20)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${rem(16)};
    padding-top: ${vwDesktop(40)};
    padding-bottom: ${vwDesktop(40)};
    padding-left: 0;
    padding-right: 0;
    max-width: ${vwDesktop(438)};
  }
`;

const PrimaryCta = styled.a`
  display: inline-flex;
  padding: ${rem(16)} ${rem(32)};
  justify-content: center;
  align-items: center;
  border-radius: ${rem(431)};
  background: #b5dd71;
  color: #012604;
  font-family: "GT Alpina";
  font-size: ${rem(12)};
  font-style: normal;
  font-weight: 300;

  text-decoration: none;

  &:focus-visible {
    outline: ${rem(2)} solid var(--Linen, #f6f1e7);
    outline-offset: ${rem(3)};
  }

  @media (hover: hover) {
    &:hover {
      background: #f6f1e7;
      color: #012604;
    }
  }
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${rem(16)};
  }
`;

const ImageWrap = styled.div`
  border-radius: ${vwMobile(10)};
  overflow: hidden;
  display: flex;
  justify-content: center;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: block;
    border-radius: ${vwDesktop(18)};
  }
`;

const Image = styled.img`
  display: block;

  width: ${rem(271)};
  height: ${rem(271)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${rem(640)};
    height: ${rem(674)};
  }
`;
